/**
 * 中央ペイン: データ入力グリッド
 * - データ表: KeisukeDashboardUnifiedInputGrid（長形式仮想表＋専用レイアウトを内部で切替）
 * - ツールバー「貼り付け」: クリップボードの CSV/TSV で表全体を差し替え
 * - 読込: CSV/TSV/Excel（.xlsx/.xls）。Excel出力との往復に対応
 * - Excel出力: 表を .xlsx でダウンロード
 */

import { useRef, useMemo, useCallback, useEffect, useLayoutEffect, useState, useId } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { parseCSV, parseTabSeparated } from '../../../../utils/csvParser';
import type { DataRow, InputFormTypeKey, VariableMapping } from '../../../../stores/systems/keisuke/dashboardStore';
import { useKeisukeDashboardStore } from '../../../../stores/systems/keisuke/dashboardStore';
import { gridDataHasNumericValues, trimTrailingEmptyGridRows } from '../../../../utils/keisukeGridData';
import {
  getStandardDatasetById,
  getStandardDatasetsByCategory,
  standardDatasetMatchesCurrentAnalysis,
  type StandardDatasetEntry,
} from '../../../../services/systems/keisuke/keisukeStandardDatasets';
import * as XLSX from 'xlsx';
import KeisukeDashboardUnifiedInputGrid from './KeisukeDashboardUnifiedInputGrid';
import KeisukeDashboardEmptyHeroBand from './KeisukeDashboardEmptyHeroBand';
import KeisukeDashboardResultsStrip from './KeisukeDashboardResultsStrip';
import KeisukePlateBatchPanel from './KeisukePlateBatchPanel';
import {
  KeisukePrimarySampleButton,
  KeisukeSampleDataDropdown,
  KeisukeSampleInfoDetails,
  ToolbarIconButton,
} from './KeisukeDashboardCenterToolbarBits';
import type { PlateAnalysisType, PlateWorkspacePayload } from '../../../../utils/keisukePlateBatch';
import {
  clampKeisukeDashboardDataColWidthPx,
  KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX,
  keisukeDashboardGridAreaUsesShrinkWrap,
} from './keisukeDashboardDataGridLayout';
import { KEISUKE_DASHBOARD_SAMPLE_LOADED_EVENT } from './KeisukeDashboardBarChartSettingsContext';
import {
  coerceInputFormTypeToMatchAnalysis,
  getStandardDatasetLayoutPatch,
  renameGridColumnAtIndex,
} from '../../../../utils/keisukeInputFormUtils';
import { isPaperTextLocale, type PaperTextLocale } from '../../../../utils/keisukePaperText';
import {
  isKeisukeGenericValueColumnKey,
  remapStockLongFormatGrid,
} from '../../../../utils/keisukeStockColumnLabels';
import { resolveTTestTabularImport } from './keisukeDashboardTabularImport';
import styles from './KeisukeDashboardCenterPane.module.scss';

function paperLocaleFromUiLanguage(lang: string): PaperTextLocale {
  if (isPaperTextLocale(lang)) return lang;
  const short = lang.split('-')[0];
  if (short === 'zh') return 'zh-CN';
  if (isPaperTextLocale(short)) return short as PaperTextLocale;
  return 'en';
}

/** 空／shrink 途中入力／長形式充填で背景シェルを切り替える */
function resolveCenterWorkspaceClass(hasNumericData: boolean, shrinkGridShell: boolean): string {
  if (!hasNumericData) return styles.emptyWorkspace;
  if (shrinkGridShell) return `${styles.filledWorkspace} ${styles.filledWorkspaceShrink}`;
  return styles.filledWorkspace;
}

export default function KeisukeDashboardCenterPane() {
  const { t, i18n } = useTranslation();
  const csvImportInputId = useId();
  const gridAreaRef = useRef<HTMLDivElement>(null);

  const {
    gridData,
    gridHeaders,
    isCalculating,
    analysisOptions,
    inputFormType,
    twoWayNPerCell,
    twoWayNLevelsA,
    twoWayNLevelsB,
    tTestNPerGroup,
    oneWayNPerGroup,
    oneWayGroupCount,
    setGridData,
    setGridHeaders,
    setGridDataDebounced,
    setAnalysisOptions,
    setLastErrorMessage,
    setInputFormType,
    setTwoWayNPerCell,
    setTwoWayNLevelsA,
    setTwoWayNLevelsB,
    setTTestNPerGroup,
    setOneWayNPerGroup,
    setOneWayGroupCount,
    doseResponseNRows,
    setDoseResponseNRows,
    survivalNRows,
    setSurvivalNRows,
    repeatedNRows,
    setRepeatedNRows,
    loadDoseResponseWorkspace,
    resultCache,
    lastErrorMessage,
  } = useKeisukeDashboardStore(
    useShallow((s) => ({
      gridData: s.gridData,
      gridHeaders: s.gridHeaders,
      isCalculating: s.isCalculating,
      analysisOptions: s.analysisOptions,
      inputFormType: s.inputFormType,
      twoWayNPerCell: s.twoWayNPerCell,
      twoWayNLevelsA: s.twoWayNLevelsA,
      twoWayNLevelsB: s.twoWayNLevelsB,
      tTestNPerGroup: s.tTestNPerGroup,
      oneWayNPerGroup: s.oneWayNPerGroup,
      oneWayGroupCount: s.oneWayGroupCount,
      setGridData: s.setGridData,
      setGridHeaders: s.setGridHeaders,
      setGridDataDebounced: s.setGridDataDebounced,
      setAnalysisOptions: s.setAnalysisOptions,
      setLastErrorMessage: s.setLastErrorMessage,
      setInputFormType: s.setInputFormType,
      setTwoWayNPerCell: s.setTwoWayNPerCell,
      setTwoWayNLevelsA: s.setTwoWayNLevelsA,
      setTwoWayNLevelsB: s.setTwoWayNLevelsB,
      setTTestNPerGroup: s.setTTestNPerGroup,
      setOneWayNPerGroup: s.setOneWayNPerGroup,
      setOneWayGroupCount: s.setOneWayGroupCount,
      doseResponseNRows: s.doseResponseNRows,
      setDoseResponseNRows: s.setDoseResponseNRows,
      survivalNRows: s.survivalNRows,
      setSurvivalNRows: s.setSurvivalNRows,
      repeatedNRows: s.repeatedNRows,
      setRepeatedNRows: s.setRepeatedNRows,
      loadDoseResponseWorkspace: s.loadDoseResponseWorkspace,
      resultCache: s.resultCache,
      lastErrorMessage: s.lastErrorMessage,
    }))
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentSampleId, setCurrentSampleId] = useState<string | null>(null);
  /** 長形式・t検定2列・1要因・2要因のデータ列幅を共有（ドラッグで変更） */
  const [longFormatTwoColWidthPx, setLongFormatTwoColWidthPx] = useState(
    KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX
  );

  const hasNumericData = useMemo(
    () => gridDataHasNumericValues(gridHeaders, gridData),
    [gridData, gridHeaders]
  );

  /** 空表では出典バーを残さない（ヘッダ先行更新の一瞬 false は無視） */
  useEffect(() => {
    if (hasNumericData) return;
    const timer = window.setTimeout(() => {
      const s = useKeisukeDashboardStore.getState();
      if (!gridDataHasNumericValues(s.gridHeaders, s.gridData)) {
        setCurrentSampleId(null);
      }
    }, 320);
    return () => window.clearTimeout(timer);
  }, [hasNumericData]);

  /** 解析タイプ切替後、載っているサンプルが現解析に合わなければ出典を消す */
  useEffect(() => {
    if (!currentSampleId) return;
    const entry = getStandardDatasetById(currentSampleId);
    if (!entry || !standardDatasetMatchesCurrentAnalysis(entry, analysisOptions)) {
      setCurrentSampleId(null);
    }
  }, [
    currentSampleId,
    analysisOptions.type,
    analysisOptions.testType,
    analysisOptions.anovaType,
  ]);

  const commitColumnRenameAtIndex = useCallback(
    (index: number, rawName: string): boolean => {
      const result = renameGridColumnAtIndex(
        index,
        rawName,
        gridHeaders,
        gridData,
        analysisOptions.mapping
      );
      if (!result.ok) return false;
      setGridHeaders(result.gridHeaders);
      setGridData(result.gridData);
      if (result.mapping) {
        setAnalysisOptions({ mapping: result.mapping }, { runAnalysis: false });
      }
      useKeisukeDashboardStore.getState().runCurrentAnalysis();
      return true;
    },
    [
      gridHeaders,
      gridData,
      analysisOptions.mapping,
      setGridHeaders,
      setGridData,
      setAnalysisOptions,
    ]
  );

  const handleRecalc = useCallback(() => {
    setLastErrorMessage(null);
    useKeisukeDashboardStore.getState().runCurrentAnalysis();
  }, [setLastErrorMessage]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** 左ペインの解析タイプ（t検定 / ANOVA の種類など）が変わったときに同期するキー */
  const handleLoadSampleData = useCallback(
    (entry: StandardDatasetEntry) => {
      const paperTextLocale = paperLocaleFromUiLanguage(i18n.language);
      const opt = entry.suggestedOptions;
      const st = opt?.type;

      let nextForm: InputFormTypeKey | null = null;
      let resolvedHeaders = entry.columns;
      let resolvedData = entry.data;
      let resolvedMapping = opt?.mapping;

      if (st === 'anova') {
        if (opt.anovaType === 'twoWay') nextForm = 'twoWayBlocks';
        else if (opt.anovaType === 'repeatedMeasures') nextForm = 'repeatedForm';
        else nextForm = 'oneWayColumns';
      } else if (st === 't-test') {
        const testType = (opt?.testType as 'unpaired' | 'paired') ?? 'unpaired';
        const resolved = resolveTTestTabularImport({
          headers: entry.columns,
          rows: entry.data,
          testType,
          suggestedMapping: opt?.mapping ?? null,
        });
        nextForm = resolved.inputFormType;
        resolvedHeaders = resolved.headers;
        resolvedData = resolved.data;
        resolvedMapping = resolved.mapping;
      } else if (st === 'dose-response') {
        nextForm = 'doseResponseForm';
      } else if (st === 'survival') {
        nextForm = 'survivalForm';
      } else if (
        st === 'longitudinal' ||
        st === 'regression' ||
        st === 'basic-stats' ||
        st === 'qpcr' ||
        st === 'bar-chart'
      ) {
        nextForm = 'longFormat';
      }

      /**
       * 入力形式を先に確定してから表を載せる（2列表が長形式サンプルを group/value に潰す競合を防ぐ）。
       * flushSync で旧フォームをアンマウントしてから grid を更新する。
       */
      if (nextForm) {
        flushSync(() => {
          setInputFormType(nextForm!);
        });
      }
      /** サンプルはデータ正。推論手法・Welch/Student はユーザー選択を維持する */
      const prevOpts = useKeisukeDashboardStore.getState().analysisOptions;
      const keepTTestPrefs =
        (entry.suggestedOptions?.type ?? prevOpts.type) === 't-test'
          ? {
              ttestInferential: prevOpts.ttestInferential,
              equalVariance: prevOpts.equalVariance,
            }
          : {};
      setAnalysisOptions(
        {
          ...entry.suggestedOptions,
          paperTextLocale,
          ...keepTTestPrefs,
          ...(resolvedMapping ? { mapping: resolvedMapping } : {}),
        },
        { runAnalysis: false }
      );
      setGridHeaders(resolvedHeaders);
      setGridData(resolvedData, { runAnalysis: false });
      setLastErrorMessage(null);
      setCurrentSampleId(entry.id);
      /** 結果タブは維持（論文用を読んで別サンプルを開いても飛ばない） */
      useKeisukeDashboardStore.setState(getStandardDatasetLayoutPatch(entry));
      useKeisukeDashboardStore.getState().runCurrentAnalysis();
      /** 主経路は平均＋誤差棒。箱ひげ等の永続表示をサンプル読込でリセット */
      window.dispatchEvent(new CustomEvent(KEISUKE_DASHBOARD_SAMPLE_LOADED_EVENT));
    },
    [
      i18n.language,
      setGridHeaders,
      setGridData,
      setAnalysisOptions,
      setLastErrorMessage,
      setInputFormType,
    ]
  );

  const { tutorial, verification } = useMemo(() => getStandardDatasetsByCategory(), []);

  // 選択された解析タイプ・ANOVAサブタイプに適合するサンプルのみ表示（t検定 / 1way / 2way など）
  const tutorialForType = useMemo(
    () => tutorial.filter((e) => standardDatasetMatchesCurrentAnalysis(e, analysisOptions)),
    [tutorial, analysisOptions]
  );
  const verificationForType = useMemo(
    () => verification.filter((e) => standardDatasetMatchesCurrentAnalysis(e, analysisOptions)),
    [verification, analysisOptions]
  );
  const hasAnySample = tutorialForType.length > 0 || verificationForType.length > 0;
  const primarySample = tutorialForType[0] ?? verificationForType[0] ?? null;

  const handleLoadPrimarySample = useCallback(() => {
    if (!primarySample) return;
    handleLoadSampleData(primarySample);
    try {
      sessionStorage.setItem('keisuke_guest_banner_dismissed', '1');
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent('keisuke-guest-banner-dismiss'));
  }, [primarySample, handleLoadSampleData]);

  useEffect(() => {
    const onRequest = () => handleLoadPrimarySample();
    window.addEventListener('keisuke-load-primary-sample', onRequest);
    return () => window.removeEventListener('keisuke-load-primary-sample', onRequest);
  }, [handleLoadPrimarySample]);

  /** CSV/TSV テキストを表に反映。ヘッダ無しは false。t検定は形に合わせて入力形式を合わせる。 */
  const applyTabularTextToGrid = useCallback(
    (text: string): boolean => {
      const hasTabs = /\t/.test(text);
      const parsed = hasTabs ? parseTabSeparated(text) : parseCSV(text);
      if (parsed.headers.length === 0) {
        setLastErrorMessage(t('keisuke.dashboard.errorNoHeaders'));
        return false;
      }
      const data: DataRow[] = parsed.rows.map((row) => {
        const obj: DataRow = {};
        parsed.headers.forEach((h, i) => {
          obj[h] = row[i] ?? '';
        });
        return obj;
      });

      if (analysisOptions.type === 't-test') {
        const testType = (analysisOptions.testType as 'unpaired' | 'paired') ?? 'unpaired';
        const resolved = resolveTTestTabularImport({
          headers: parsed.headers,
          rows: data,
          testType,
        });
        /** サンプル読込と同様、旧フォームを先に外してから表を載せる */
        flushSync(() => {
          setInputFormType(resolved.inputFormType);
        });
        setAnalysisOptions({ mapping: resolved.mapping }, { runAnalysis: false });
        setGridHeaders(resolved.headers);
        setGridData(resolved.data);
      } else {
        setGridHeaders(parsed.headers);
        setGridData(data);
      }
      setCurrentSampleId(null);
      return true;
    },
    [
      analysisOptions.type,
      analysisOptions.testType,
      setGridHeaders,
      setGridData,
      setInputFormType,
      setAnalysisOptions,
      setLastErrorMessage,
      t,
    ]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      setLastErrorMessage(null);
      if (!file) return;

      const fail = () => setLastErrorMessage(t('keisuke.dashboard.errorImport'));
      const lower = file.name.toLowerCase();
      const isExcel = lower.endsWith('.xlsx') || lower.endsWith('.xls');

      const reader = new FileReader();
      reader.onerror = fail;
      reader.onload = () => {
        try {
          if (isExcel) {
            const wb = XLSX.read(reader.result, { type: 'array' });
            const sheetName = wb.SheetNames[0];
            const sheet = sheetName ? wb.Sheets[sheetName] : undefined;
            if (!sheet) {
              fail();
              return;
            }
            /** 先頭シートを CSV 化し、既存の表正規化経路へ載せる（Excel出力との往復） */
            applyTabularTextToGrid(XLSX.utils.sheet_to_csv(sheet));
            return;
          }
          applyTabularTextToGrid(String(reader.result ?? ''));
        } catch {
          fail();
        }
      };
      if (isExcel) reader.readAsArrayBuffer(file);
      else reader.readAsText(file, 'UTF-8');
    },
    [applyTabularTextToGrid, setLastErrorMessage, t]
  );

  const handleExportExcel = useCallback(() => {
    if (gridHeaders.length === 0) return;
    const trimmed = trimTrailingEmptyGridRows(gridHeaders, gridData);
    const headerRow = [...gridHeaders];
    const rows: (string | number)[][] = [headerRow];
    trimmed.forEach((row) => {
      rows.push(gridHeaders.map((h) => row[h] ?? ''));
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    const filename = `keisuke_data_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [gridHeaders, gridData]);

  /** デバウンス適用後の rAF コミット（点滅防止）。マッピング／行数同期は任意。 */
  const commitCompactFormGrid = useCallback(
    (
      data: DataRow[],
      headers: string[],
      opts?: {
        mapping?: VariableMapping;
        /** 塗りつぶし行数が変わったときだけ呼ぶ（用量反応・生存・反復） */
        syncFilledRowCount?: (filled: number) => void;
      }
    ) => {
      requestAnimationFrame(() => {
        setLastErrorMessage(null);
        setGridHeaders(headers);
        setGridDataDebounced(data);
        if (opts?.mapping) {
          setAnalysisOptions({ mapping: opts.mapping }, { runAnalysis: false });
        }
        if (opts?.syncFilledRowCount) {
          const filled = data.filter((row) =>
            headers.some((h) => {
              const v = row[h];
              return v != null && String(v).trim() !== '';
            })
          ).length;
          if (filled > 0) opts.syncFilledRowCount(filled);
        }
      });
    },
    [
      setLastErrorMessage,
      setGridHeaders,
      setGridDataDebounced,
      setAnalysisOptions,
    ]
  );

  const handleFormApply = useCallback(
    (data: DataRow[], headers: string[], opts?: { paired?: boolean }) => {
      const state = useKeisukeDashboardStore.getState();
      const prevHeaders = state.gridHeaders;
      const headersChanged =
        headers.length !== prevHeaders.length || headers.some((h, i) => h !== prevHeaders[i]);
      /**
       * ヘッダだけ先に変えると、旧キーの行が一時的に「数値なし」と判定され出典が消える。
       * 列名変更時は即時 setGridData で揃える。
       */
      if (headersChanged) {
        setGridHeaders(headers);
        setGridData(data, { runAnalysis: false });
      } else {
        setGridDataDebounced(data);
      }
      if (headers.length !== 2) return;
      const paired = opts?.paired === true;
      const map = state.analysisOptions.mapping;
      const mappingUnchanged = paired
        ? map?.beforeColumn === headers[0] && map?.afterColumn === headers[1]
        : map?.groupColumn === headers[0] && map?.valueColumn === headers[1];
      if (!mappingUnchanged) {
        const preservedAxis =
          !paired &&
          map?.valueColumn &&
          map.valueColumn !== headers[0] &&
          map.valueColumn !== headers[1]
            ? map.valueColumn
            : map?.valueAxisLabel;
        /** 値列自体が測定名（体重など）なら valueAxisLabel は不要（英語の Weight が残るのを防ぐ） */
        const valueCol = headers[1]!;
        const keepAxis =
          Boolean(preservedAxis) && isKeisukeGenericValueColumnKey(valueCol);
        setAnalysisOptions(
          {
            mapping: paired
              ? { beforeColumn: headers[0], afterColumn: headers[1] }
              : {
                  groupColumn: headers[0],
                  valueColumn: valueCol,
                  ...(keepAxis ? { valueAxisLabel: preservedAxis } : {}),
                },
          },
          { runAnalysis: false }
        );
      }
      if (headersChanged) {
        useKeisukeDashboardStore.getState().runCurrentAnalysis();
      }
    },
    [setGridHeaders, setGridData, setGridDataDebounced, setAnalysisOptions]
  );

  const handleOneWayFormApply = useCallback(
    (data: DataRow[], headers: string[]) => {
      setGridHeaders(headers);
      setGridDataDebounced(data);
      if (headers.length !== 2) return;
      setAnalysisOptions(
        {
          mapping: {
            groupColumn: headers[0],
            factorAColumn: headers[0],
            valueColumn: headers[1],
          },
        },
        { runAnalysis: false }
      );
    },
    [setGridHeaders, setGridDataDebounced, setAnalysisOptions]
  );

  const handleTwoWayFormApply = useCallback(
    (data: DataRow[], headers: string[]) => {
      commitCompactFormGrid(
        data,
        headers,
        headers.length === 3
          ? {
              mapping: {
                factorAColumn: headers[0],
                factorBColumn: headers[1],
                valueColumn: headers[2],
              },
            }
          : undefined
      );
    },
    [commitCompactFormGrid]
  );

  const handleDoseResponseFormApply = useCallback(
    (data: DataRow[], headers: string[]) => {
      commitCompactFormGrid(data, headers, {
        mapping:
          headers.length >= 3
            ? {
                doseColumn: headers[0],
                effectColumn: headers[1],
                totalColumn: headers[2],
              }
            : undefined,
        syncFilledRowCount: (filled) => {
          if (filled !== doseResponseNRows) setDoseResponseNRows(filled);
        },
      });
    },
    [commitCompactFormGrid, doseResponseNRows, setDoseResponseNRows]
  );

  const handleSurvivalFormApply = useCallback(
    (data: DataRow[], headers: string[]) => {
      commitCompactFormGrid(data, headers, {
        mapping:
          headers.length >= 3
            ? {
                survivalGroupColumn: headers[0],
                groupColumn: headers[0],
                timeColumn: headers[1],
                eventColumn: headers[2],
              }
            : undefined,
        syncFilledRowCount: (filled) => {
          if (filled !== survivalNRows) setSurvivalNRows(filled);
        },
      });
    },
    [commitCompactFormGrid, survivalNRows, setSurvivalNRows]
  );

  const handleRepeatedFormApply = useCallback(
    (data: DataRow[], headers: string[]) => {
      commitCompactFormGrid(data, headers, {
        mapping:
          headers.length >= 3
            ? {
                subjectColumn: headers[0],
                factorAColumn: headers[1],
                groupColumn: headers[1],
                valueColumn: headers[2],
              }
            : undefined,
        syncFilledRowCount: (filled) => {
          if (filled !== repeatedNRows) setRepeatedNRows(filled);
        },
      });
    },
    [commitCompactFormGrid, repeatedNRows, setRepeatedNRows]
  );

  const handlePlateSampleLoad = useCallback(
    (payload: PlateWorkspacePayload) => {
      setLastErrorMessage(null);
      setCurrentSampleId(payload.sampleName);
      loadDoseResponseWorkspace({
        headers: [...payload.headers],
        rows: payload.rows as DataRow[],
        doseResponseNRows: Math.max(2, payload.rows.length),
        drType: payload.drType,
        drMethod: payload.drMethod,
        mapping: {
          doseColumn: payload.headers[0],
          effectColumn: payload.headers[1],
          totalColumn: payload.headers[2],
        },
      });
    },
    [setLastErrorMessage, loadDoseResponseWorkspace]
  );

  const analysisFormCoerceOpts = useMemo(
    () => ({ type: analysisOptions.type, anovaType: analysisOptions.anovaType }),
    [analysisOptions.type, analysisOptions.anovaType]
  );

  /** ストア値が許可外でも、同一レンダーでセレクト・表の分岐を解析に合わせる */
  const effectiveInputFormType = useMemo(
    () => coerceInputFormTypeToMatchAnalysis(inputFormType, analysisFormCoerceOpts),
    [inputFormType, analysisFormCoerceOpts]
  );

  useLayoutEffect(() => {
    if (effectiveInputFormType !== inputFormType) {
      setInputFormType(effectiveInputFormType);
    }
  }, [effectiveInputFormType, inputFormType, setInputFormType]);

  /** 長形式表示時、group/value/Weight などの stock 列名を UI 言語へ揃える */
  useLayoutEffect(() => {
    if (effectiveInputFormType !== 'longFormat') return;
    const remapped = remapStockLongFormatGrid(
      gridHeaders,
      gridData,
      analysisOptions.mapping,
      t
    );
    if (!remapped) return;
    setGridHeaders(remapped.headers);
    setGridData(remapped.data, { runAnalysis: false });
    setAnalysisOptions({ mapping: remapped.mapping }, { runAnalysis: false });
    useKeisukeDashboardStore.getState().runCurrentAnalysis();
  }, [
    effectiveInputFormType,
    gridHeaders,
    gridData,
    analysisOptions.mapping,
    t,
    setGridHeaders,
    setGridData,
    setAnalysisOptions,
  ]);

  const handlePasteFromClipboard = useCallback(async () => {
    setLastErrorMessage(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        setLastErrorMessage(t('keisuke.dashboard.errorEmptyClipboard'));
        return;
      }
      applyTabularTextToGrid(text);
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setLastErrorMessage(t('keisuke.dashboard.errorClipboardPermission'));
      } else {
        setLastErrorMessage(t('keisuke.dashboard.errorPaste'));
      }
    }
  }, [applyTabularTextToGrid, setLastErrorMessage, t]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
        if (!gridAreaRef.current?.contains(e.target as Node)) return;
        const el = e.target as HTMLElement;
        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
        const tableEl = gridAreaRef.current?.querySelector('table');
        if (tableEl?.contains(el)) return;
        e.preventDefault();
        handlePasteFromClipboard();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handlePasteFromClipboard]);

  const shrinkGridShell = keisukeDashboardGridAreaUsesShrinkWrap(effectiveInputFormType);
  /** 空／列行列の途中入力は同じ幅のカード。長形式だけ全幅シェル */
  const useCompositionCard = !hasNumericData || shrinkGridShell;
  const workspaceClass = resolveCenterWorkspaceClass(hasNumericData, shrinkGridShell);
  /** コンパクト表: 結果をカード内フッタへ（カードを列高まで伸ばし、結果帯が余白を吸収） */
  const embedResultsInComposition =
    hasNumericData && shrinkGridShell && (Boolean(resultCache) || Boolean(lastErrorMessage));
  const wrapSizeClass =
    hasNumericData && shrinkGridShell
      ? embedResultsInComposition
        ? styles.wrapShrinkFillCard
        : styles.wrapShrinkToContent
      : '';

  const gridArea = (
    <div
      ref={gridAreaRef}
      className={`${styles.gridArea}${shrinkGridShell ? ` ${styles.gridAreaShrinkWrap}` : ''}`}
    >
      {/* 計算中も表を disabled にしない（disabled の input はコピー・ペースト不可） */}
      <KeisukeDashboardUnifiedInputGrid
        inputFormType={effectiveInputFormType}
        gridData={gridData}
        gridHeaders={gridHeaders}
        disabled={false}
        sampleKey={currentSampleId}
        longFormatTwoColWidthPx={longFormatTwoColWidthPx}
        onLinkedDataColumnWidthCommit={(px) =>
          setLongFormatTwoColWidthPx(clampKeisukeDashboardDataColWidthPx(px))
        }
        onLongFormatDataChange={setGridDataDebounced}
        onRenameColumn={commitColumnRenameAtIndex}
        onTTestApply={handleFormApply}
        onOneWayApply={handleOneWayFormApply}
        onTwoWayApply={handleTwoWayFormApply}
        onDoseResponseApply={handleDoseResponseFormApply}
        doseResponseNRows={doseResponseNRows}
        onDoseResponseNRowsChange={setDoseResponseNRows}
        onSurvivalApply={handleSurvivalFormApply}
        survivalNRows={survivalNRows}
        onSurvivalNRowsChange={setSurvivalNRows}
        onRepeatedApply={handleRepeatedFormApply}
        repeatedNRows={repeatedNRows}
        onRepeatedNRowsChange={setRepeatedNRows}
        mapping={analysisOptions.mapping}
        testType={(analysisOptions.testType as 'unpaired' | 'paired') ?? 'unpaired'}
        twoWayNPerCell={twoWayNPerCell}
        onTwoWayNPerCellChange={setTwoWayNPerCell}
        twoWayNLevelsA={twoWayNLevelsA}
        onTwoWayNLevelsAChange={setTwoWayNLevelsA}
        twoWayNLevelsB={twoWayNLevelsB}
        onTwoWayNLevelsBChange={setTwoWayNLevelsB}
        tTestNPerGroup={tTestNPerGroup}
        onTTestNPerGroupChange={setTTestNPerGroup}
        oneWayNPerGroup={oneWayNPerGroup}
        onOneWayNPerGroupChange={setOneWayNPerGroup}
        oneWayGroupCount={oneWayGroupCount}
        onOneWayGroupCountChange={setOneWayGroupCount}
      />
    </div>
  );

  return (
    <div className={`${styles.wrap}${wrapSizeClass ? ` ${wrapSizeClass}` : ''}`}>
      {/* 空状態はツールバーを出さず、表の上のヒーローに主CTAを置く */}
      {hasNumericData && (
        <div
          className={`${styles.toolbar}${useCompositionCard ? ` ${styles.toolbarOverCard}` : ''}`}
          data-keisuke-center-toolbar
        >
          {/* 途中入力では緑CTAを残す。結果が出たら消し、誤ってサンプル上書きしない */}
          {useCompositionCard && primarySample && !resultCache ? (
            <KeisukePrimarySampleButton
              className={styles.toolbarPrimarySample}
              onClick={handleLoadPrimarySample}
              title={t(primarySample.nameKey)}
            />
          ) : null}
          <div className={styles.toolbarGroup}>
            <ToolbarIconButton
              icon="fa-file-import"
              label={t('keisuke.dashboard.toolbar.import')}
              title={t('keisuke.dashboard.tooltips.importData')}
              onClick={handleImportClick}
            />
            <ToolbarIconButton
              icon="fa-paste"
              label={t('keisuke.dashboard.toolbar.paste')}
              title={t('keisuke.dashboard.tooltips.paste')}
              onClick={handlePasteFromClipboard}
            />
            <ToolbarIconButton
              icon="fa-file-excel"
              label={t('keisuke.dashboard.toolbar.excel')}
              title={t('keisuke.dashboard.tooltips.exportExcel')}
              onClick={handleExportExcel}
              disabled={gridHeaders.length === 0}
            />
          </div>

          <div className={styles.toolbarDivider} />
          <KeisukeSampleDataDropdown
            id="keisuke-sample-data-dropdown"
            align="end"
            variant="outline-secondary"
            toggleClassName={styles.toolbarIconBtn}
            tutorialForType={tutorialForType}
            verificationForType={verificationForType}
            hasAnySample={hasAnySample}
            onLoadSampleData={handleLoadSampleData}
            excludeSampleId={primarySample?.id}
          />

          {/* 自動再計算が正。失敗時だけ手動再実行（計算中表示は下の status） */}
          {lastErrorMessage ? (
            <ToolbarIconButton
              icon="fa-sync-alt"
              iconOnly
              title={t('keisuke.dashboard.tooltips.recalc')}
              ariaLabel={t('keisuke.dashboard.toolbar.recalc')}
              onClick={handleRecalc}
              disabled={isCalculating}
            />
          ) : null}
          {isCalculating && !resultCache ? (
            <span className={styles.calculating} role="status">
              {t('keisuke.dashboard.calculating')}
            </span>
          ) : null}
          {currentSampleId ? (
            <div className={styles.toolbarMeta}>
              <KeisukeSampleInfoDetails sampleId={currentSampleId} />
            </div>
          ) : null}
        </div>
      )}
      <input
        ref={fileInputRef}
        id={csvImportInputId}
        name="keisuke-dashboard-import-csv"
        type="file"
        accept=".csv,.txt,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="d-none"
        aria-hidden
        tabIndex={-1}
        onChange={handleFileChange}
      />
      {analysisOptions.type === 'dose-response' && (
        <KeisukePlateBatchPanel
          initialAnalysis={
            (['tcid50', 'ld50', 'ec50', 'ec90'].includes(String(analysisOptions.drType))
              ? (analysisOptions.drType as PlateAnalysisType)
              : 'tcid50')
          }
          onLoadSample={handlePlateSampleLoad}
        />
      )}
      <div
        className={`${workspaceClass}${
          !hasNumericData ? ` ${styles.emptyWorkspaceWithDock}` : ''
        }`}
      >
        {/*
          表の親子は常に同じ（空→数値で別ブランチにするとアンマウントされ値が消える）。
          列行列の途中入力も composition 幅を維持（全幅ジャンプを避ける）。
          空状態は CSS でカード＋予告帯を横並びにし、gridArea は常に同一ツリー。
        */}
        <div
          className={
            useCompositionCard
              ? styles.emptyWorkspaceComposition
              : styles.filledWorkspaceGrid
          }
          {...(!hasNumericData ? { 'data-keisuke-empty-composition': '' } : {})}
        >
          {!hasNumericData ? (
            <KeisukeDashboardEmptyHeroBand
              primarySample={primarySample}
              hasAnySample={hasAnySample}
              tutorialForType={tutorialForType}
              verificationForType={verificationForType}
              onLoadPrimarySample={handleLoadPrimarySample}
              onLoadSampleData={handleLoadSampleData}
              onImportClick={handleImportClick}
              onPasteFromClipboard={handlePasteFromClipboard}
            />
          ) : null}
          {hasNumericData && shrinkGridShell && !resultCache ? (
            <p
              className={styles.partialInputHint}
              role="status"
              data-keisuke-partial-hint
            >
              {t(
                analysisOptions.type === 't-test'
                  ? 'keisuke.workspace.partialInputHintTTest'
                  : 'keisuke.workspace.partialInputHint'
              )}
            </p>
          ) : null}
          <div
            className={
              useCompositionCard
                ? styles.emptyWorkspaceGrid
                : styles.filledWorkspaceGridHost
            }
          >
            {gridArea}
          </div>
          {embedResultsInComposition ? (
            <div
              className={styles.compositionResults}
              data-keisuke-results-pane
            >
              <KeisukeDashboardResultsStrip embedded />
            </div>
          ) : null}
        </div>
        {!hasNumericData ? (
          primarySample ? (
            <button
              type="button"
              className={styles.emptyWorkspaceDockHint}
              onClick={handleLoadPrimarySample}
              aria-label={`${t('keisuke.workspace.emptyDockHint')} ${t('keisuke.workspace.emptyDockHintCta')}`}
            >
              <i className={`fas fa-chart-bar ${styles.emptyWorkspaceDockHintIcon}`} aria-hidden />
              <span>{t('keisuke.workspace.emptyDockHint')}</span>
            </button>
          ) : (
            <p className={styles.emptyWorkspaceDockHint} role="note">
              <i className={`fas fa-chart-bar ${styles.emptyWorkspaceDockHintIcon}`} aria-hidden />
              <span>{t('keisuke.workspace.emptyDockHint')}</span>
            </p>
          )
        ) : null}
      </div>
    </div>
  );
}
